import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CreatePlaceholderTemplateRequest,
  CreatePlaceholderTemplateResponse,
  UpdatePlaceholderTemplateRequest,
  UpdatePlaceholderTemplateResponse,
  ListHomesByCompanyRequest,
  ListHomesByCompanyResponse,
  ListIfcFilesByHomeIdRequest,
  ListIfcFilesByHomeIdResponse,
  PlaceholderTemplateDetail,
  PlaceholderTemplateAffectedHomeDto,
  PlaceholderTemplateSourceMappingDto,
  Home,
  HomeIfcFile,
  HomeIfcFileType,
  SituViewWebApiClient
} from 'situview-webapi-client';
import { CompanyType, ICompany } from 'identity-webapi-client';
import { AuthService } from 'src/app/services';
import { CompanyService } from 'src/app/services/company.service';

export interface PlaceholderTemplateDialogData {
  isEdit: boolean;
  companyId: string | undefined;
  template?: PlaceholderTemplateDetail;
}

interface AffectedHomeEntry {
  homeId: string;
  homeName: string;
  isExisting: boolean;
  isNew: boolean;
}

interface SourceMappingEntry {
  sourceCompanyId: string;
  sourceHomeId: string;
  sourceHomeName: string;
  selectedIfcFileIds: string[];
  // Local UI state
  availableHomes: Home[];
  loadingHomes: boolean;
  availableFiles: HomeIfcFile[];
  loadingFiles: boolean;
}

@Component({
  selector: 'app-placeholder-template-dialog',
  templateUrl: './placeholder-template-dialog.component.html',
  styleUrls: ['./placeholder-template-dialog.component.less']
})
export class PlaceholderTemplateDialogComponent implements OnInit {
  name = '';
  renderCode = '';
  description = '';
  affectedHomes: AffectedHomeEntry[] = [];
  sourceMappings: SourceMappingEntry[] = [];
  saving = false;

  // Home dropdown state for affected homes (current company only)
  allHomes: Home[] = [];
  loadingHomes = false;
  newAffectedHomeId = '';
  newHomeName = '';

  // Sibling companies for source mappings
  siblingCompanies: ICompany[] = [];

  // IFC file type enum
  homeIfcFileType = HomeIfcFileType;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PlaceholderTemplateDialogData,
    private dialogRef: MatDialogRef<PlaceholderTemplateDialogComponent>,
    private situViewApiClient: SituViewWebApiClient,
    private authService: AuthService,
    private companyService: CompanyService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadHomes();
    this.loadSiblingCompanies();

    if (this.data.isEdit && this.data.template) {
      const t = this.data.template;
      this.name = t.name ?? '';
      this.renderCode = t.renderCode ?? '';
      this.description = t.description ?? '';
      this.affectedHomes = (t.affectedHomes ?? []).map(h => ({
        homeId: h.homeId ?? '',
        homeName: h.homeName ?? '',
        isExisting: h.isExisting ?? false,
        isNew: false
      }));
      this.sourceMappings = (t.sourceMappings ?? []).map(m => ({
        sourceCompanyId: '',
        sourceHomeId: m.sourceHomeId ?? '',
        sourceHomeName: m.sourceHomeName ?? '',
        selectedIfcFileIds: (m.sourceFiles ?? []).map(
          f => f.sourceHomeIfcFileId ?? ''
        ),
        availableHomes: [],
        loadingHomes: false,
        availableFiles: [],
        loadingFiles: false
      }));

      // Load homes and IFC files for each existing source mapping
      // We need to resolve the company from the builderName in the detail
      this.sourceMappings.forEach((m, i) => {
        const mappingDetail = t.sourceMappings?.[i];
        if (m.sourceHomeId && mappingDetail) {
          // Try to find the company by builder name once siblings are loaded
          this.companyService.companies$.subscribe(companies => {
            if (!companies) return;
            const company = companies.find(
              c => c.name === mappingDetail.builderName
            );
            if (company) {
              m.sourceCompanyId = company.id ?? '';
              this.loadSourceHomes(m);
            }
          });
          this.loadIfcFiles(m);
        }
      });
    }
  }

  get dialogTitle(): string {
    return this.data.isEdit
      ? `Edit \u2014 ${this.data.template?.name}`
      : 'New Placeholder Template';
  }

  get canSave(): boolean {
    const hasName = !!this.name.trim();
    const hasRenderCode = !!this.renderCode.trim();
    const hasAffectedHomes = this.affectedHomes.length > 0;
    const hasSourceMappings = this.sourceMappings.length > 0;
    const hasFiles = this.sourceMappings.some(
      m => m.selectedIfcFileIds.length > 0
    );
    return hasName && hasRenderCode && hasAffectedHomes
      && hasSourceMappings && hasFiles;
  }

  get totalSelectedMasterFiles(): number {
    return this.sourceMappings.reduce((sum, m) => {
      return sum + m.selectedIfcFileIds.filter(id =>
        this.isFileMaster(id, m)
      ).length;
    }, 0);
  }

  get totalSelectedOptionFiles(): number {
    return this.sourceMappings.reduce((sum, m) => {
      return sum + m.selectedIfcFileIds.filter(id =>
        !this.isFileMaster(id, m)
      ).length;
    }, 0);
  }

  get totalSelectedFiles(): number {
    return this.sourceMappings.reduce(
      (sum, m) => sum + m.selectedIfcFileIds.length, 0
    );
  }

  // --- Home loading ---

  loadHomes(): void {
    if (!this.data.companyId) return;
    this.loadingHomes = true;

    const request = new ListHomesByCompanyRequest();
    request.companyId = this.data.companyId;

    this.situViewApiClient.listHomesByCompany(request)
      .then((response: ListHomesByCompanyResponse) => {
        this.allHomes = (response.homes ?? []).filter(h => !h.isHidden);
        this.loadingHomes = false;
      })
      .catch(() => {
        this.loadingHomes = false;
        this.snackBar.open('Failed to load homes', 'OK', {
          duration: 3000
        });
      });
  }

  // --- Affected homes ---

  addAffectedHome(): void {
    if (!this.newAffectedHomeId) return;
    if (this.affectedHomes.some(
      h => h.homeId === this.newAffectedHomeId
    )) {
      this.snackBar.open('Home already added', 'OK', { duration: 2000 });
      return;
    }

    const home = this.allHomes.find(
      h => h.id === this.newAffectedHomeId
    );
    if (!home) return;

    this.affectedHomes.push({
      homeId: home.id ?? '',
      homeName: home.name ?? '',
      isExisting: !home.isPlaceholder,
      isNew: false
    });

    this.newAffectedHomeId = '';
  }

  addNewHome(): void {
    const name = this.newHomeName.trim();
    if (!name) return;
    if (this.affectedHomes.some(
      h => h.homeName.toLowerCase() === name.toLowerCase()
    )) {
      this.snackBar.open('Home already added', 'OK', { duration: 2000 });
      return;
    }

    this.affectedHomes.push({
      homeId: '',
      homeName: name,
      isExisting: false,
      isNew: true
    });

    this.newHomeName = '';
  }

  removeAffectedHome(index: number): void {
    this.affectedHomes.splice(index, 1);
  }

  // --- Sibling companies ---

  loadSiblingCompanies(): void {
    if (this.authService.hasSituAccess()) {
      // Situ users: can source from any builder company
      this.companyService.companies$.subscribe(companies => {
        if (!companies) return;
        this.siblingCompanies = companies
          .filter(c => (c as any).type !== CompanyType.Manufacturer)
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      });
    } else {
      // Builder users: filter to companies in the same family
      const userCompanies = this.authService.listCompanies();
      this.companyService.companies$.subscribe(companies => {
        if (!companies || !this.data.companyId) return;
        const current = companies.find(
          c => c.id === this.data.companyId
        );
        if (!current) return;
        const parentId = current.parentCompanyId ?? current.id;
        const allAvailable = companies.length > 0 ? companies : userCompanies;
        this.siblingCompanies = allAvailable.filter(c => {
          const isBuilder = (c as any).type !== CompanyType.Manufacturer;
          const isSameFamily = c.id === parentId
            || c.parentCompanyId === parentId
            || c.id === current.id;
          return isBuilder && isSameFamily;
        }).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      });
    }
  }

  // --- Source mappings ---

  addSourceMapping(): void {
    this.sourceMappings.push({
      sourceCompanyId: this.data.companyId ?? '',
      sourceHomeId: '',
      sourceHomeName: '',
      selectedIfcFileIds: [],
      availableHomes: [],
      loadingHomes: false,
      availableFiles: [],
      loadingFiles: false
    });
    // Auto-load homes for the default company
    const newMapping = this.sourceMappings[this.sourceMappings.length - 1];
    if (newMapping.sourceCompanyId) {
      this.loadSourceHomes(newMapping);
    }
  }

  onSourceCompanyChange(mapping: SourceMappingEntry): void {
    mapping.sourceHomeId = '';
    mapping.sourceHomeName = '';
    mapping.selectedIfcFileIds = [];
    mapping.availableFiles = [];
    mapping.availableHomes = [];
    if (mapping.sourceCompanyId) {
      this.loadSourceHomes(mapping);
    }
  }

  loadSourceHomes(mapping: SourceMappingEntry): void {
    mapping.loadingHomes = true;
    const request = new ListHomesByCompanyRequest();
    request.companyId = mapping.sourceCompanyId;
    this.situViewApiClient.listHomesByCompany(request)
      .then((response: ListHomesByCompanyResponse) => {
        mapping.availableHomes = (response.homes ?? []).filter(h => !h.isHidden);
        mapping.loadingHomes = false;
      })
      .catch(() => {
        mapping.loadingHomes = false;
        this.snackBar.open('Failed to load homes', 'OK', {
          duration: 3000
        });
      });
  }

  removeSourceMapping(index: number): void {
    this.sourceMappings.splice(index, 1);
  }

  onSourceHomeChange(mapping: SourceMappingEntry): void {
    const home = mapping.availableHomes.find(
      h => h.id === mapping.sourceHomeId
    );
    mapping.sourceHomeName = home?.name ?? '';
    mapping.selectedIfcFileIds = [];
    mapping.availableFiles = [];

    if (mapping.sourceHomeId) {
      this.loadIfcFiles(mapping);
    }
  }

  loadIfcFiles(mapping: SourceMappingEntry): void {
    mapping.loadingFiles = true;

    const request = new ListIfcFilesByHomeIdRequest();
    request.companyId = mapping.sourceCompanyId || this.data.companyId;
    request.homeId = mapping.sourceHomeId;

    this.situViewApiClient.listIfcFilesByHomeId(request)
      .then((response: ListIfcFilesByHomeIdResponse) => {
        mapping.availableFiles = response.ifcFiles ?? [];
        mapping.loadingFiles = false;
      })
      .catch(() => {
        mapping.loadingFiles = false;
        this.snackBar.open('Failed to load IFC files', 'OK', {
          duration: 3000
        });
      });
  }

  toggleFileSelection(
    mapping: SourceMappingEntry,
    fileId: string
  ): void {
    const idx = mapping.selectedIfcFileIds.indexOf(fileId);
    if (idx >= 0) {
      mapping.selectedIfcFileIds = mapping.selectedIfcFileIds
        .filter(id => id !== fileId);
    } else {
      mapping.selectedIfcFileIds = [
        ...mapping.selectedIfcFileIds, fileId
      ];
    }
  }

  isFileSelected(
    mapping: SourceMappingEntry,
    fileId: string
  ): boolean {
    return mapping.selectedIfcFileIds.includes(fileId);
  }

  isFileMaster(fileId: string, mapping: SourceMappingEntry): boolean {
    const file = mapping.availableFiles.find(f => f.id === fileId);
    return file?.type === HomeIfcFileType.Master;
  }

  getMasterFiles(mapping: SourceMappingEntry): HomeIfcFile[] {
    return mapping.availableFiles.filter(
      f => f.type === HomeIfcFileType.Master
    );
  }

  getOptionFiles(mapping: SourceMappingEntry): HomeIfcFile[] {
    return mapping.availableFiles.filter(
      f => f.type === HomeIfcFileType.Option
    );
  }

  // --- Save ---

  onSave(): void {
    if (!this.canSave || this.saving) return;
    this.saving = true;

    if (this.data.isEdit) {
      this.updateTemplate();
    } else {
      this.createTemplate();
    }
  }

  private createTemplate(): void {
    const request = new CreatePlaceholderTemplateRequest();
    request.companyId = this.data.companyId;
    request.name = this.name.trim();
    request.renderCode = this.renderCode.trim();
    request.description = this.description.trim();
    request.affectedHomes = this.affectedHomes
      .filter(h => !h.isNew)
      .map(h => {
        const dto = new PlaceholderTemplateAffectedHomeDto();
        dto.homeId = h.homeId;
        return dto;
      });
    request.newHomeNames = this.affectedHomes
      .filter(h => h.isNew)
      .map(h => h.homeName);
    request.sourceMappings = this.sourceMappings
      .filter(m => m.sourceHomeId)
      .map(m => {
        const dto = new PlaceholderTemplateSourceMappingDto();
        dto.sourceHomeId = m.sourceHomeId;
        dto.selectedIfcFileIds = m.selectedIfcFileIds;
        return dto;
      });

    this.situViewApiClient.createPlaceholderTemplate(request)
      .then((response: CreatePlaceholderTemplateResponse) => {
        if (response.success) {
          const copied = response.filesCopied ?? 0;
          const skipped = response.filesSkipped ?? 0;
          let msg: string;
          if (response.initialSyncSucceeded) {
            msg = `Template created — synced ${copied} file${copied !== 1 ? 's' : ''}`;
            if (skipped > 0) {
              msg += `, ${skipped} skipped (originals exist)`;
            }
          } else {
            msg = 'Template created — initial sync failed, click Sync to retry';
          }
          this.snackBar.open(msg, 'OK', { duration: 4000 });
          this.dialogRef.close(true);
        } else {
          this.snackBar.open('Failed to create template', 'OK', {
            duration: 3000
          });
        }
        this.saving = false;
      })
      .catch(() => {
        this.saving = false;
        this.snackBar.open('Could not create template', 'OK', {
          duration: 3000
        });
      });
  }

  private updateTemplate(): void {
    const request = new UpdatePlaceholderTemplateRequest();
    request.companyId = this.data.companyId;
    request.templateId = this.data.template?.id;
    request.name = this.name.trim();
    request.renderCode = this.renderCode.trim();
    request.description = this.description.trim();
    request.affectedHomes = this.affectedHomes
      .filter(h => !h.isNew)
      .map(h => {
        const dto = new PlaceholderTemplateAffectedHomeDto();
        dto.homeId = h.homeId;
        return dto;
      });
    request.newHomeNames = this.affectedHomes
      .filter(h => h.isNew)
      .map(h => h.homeName);
    request.sourceMappings = this.sourceMappings
      .filter(m => m.sourceHomeId)
      .map(m => {
        const dto = new PlaceholderTemplateSourceMappingDto();
        dto.sourceHomeId = m.sourceHomeId;
        dto.selectedIfcFileIds = m.selectedIfcFileIds;
        return dto;
      });

    this.situViewApiClient.updatePlaceholderTemplate(request)
      .then((response: UpdatePlaceholderTemplateResponse) => {
        if (response.success) {
          this.snackBar.open('Template updated', 'OK', {
            duration: 2000
          });
          this.dialogRef.close(true);
        } else {
          this.snackBar.open('Failed to update template', 'OK', {
            duration: 3000
          });
        }
        this.saving = false;
      })
      .catch(() => {
        this.saving = false;
        this.snackBar.open('Could not update template', 'OK', {
          duration: 3000
        });
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
